/**
 * What the board's two scroll containers do while a card is being dragged.
 *
 * They scroll at their own rates, because a reader crossing the board and a reader going
 * down a cell are doing different things. Crossing the board is travel: a column is a
 * wide target and the reader can see which one they are heading for. Going down a cell is
 * aiming, at cards a fraction of that size, and a cell ranked by hand asks the reader to
 * stop between two of them.
 *
 * So a drag that reaches one from the other changes pace. Both run at once wherever the
 * pointer is near an inline edge of the board and low in a cell, and a card carried into
 * that corner travels further sideways than it does downwards. The one place the two
 * answer the same axis is the last stretch of the board's own lower edge over a cell that
 * ends there, where the board reaches for the next swimlane faster than the cell's cards
 * pass under the card in hand.
 *
 * A caller states either through `autoScrollRate` and `cellAutoScrollRate` on
 * `BoardProvider`, and one that states neither gets the two below.
 */

/**
 * Pixels per animation frame at full velocity, across the board and down it to reach a
 * swimlane that is off screen.
 *
 * Frames arrive at the display's refresh rate, so this is 600 pixels a second where they
 * come at 60Hz and proportionally more where they come faster. At that rate full velocity
 * crosses one of `@github-ui/board`'s 22rem columns in a little under six tenths of a
 * second, and one frame moves a card a thirty-fifth of a column, which reads as movement
 * rather than as a jump.
 *
 * It is a ceiling rather than the usual rate: velocity is the fraction of the band the
 * pointer has crossed, so the whole of this is reached only at the very edge of the
 * scrollable content and the band before it eases down to nothing.
 */
export const BOARD_AUTO_SCROLL_RATE = 10

/**
 * The same, down the cards inside one cell, which is 360 pixels a second at 60Hz.
 *
 * Slower than the board's because a card is the smaller target, and because the place
 * between two cards is a target of its own on a cell the caller ranks by hand. At this
 * rate about three and a half hundred-pixel cards pass under the card in hand each second,
 * and one frame moves it a seventeenth of a card.
 */
export const CELL_AUTO_SCROLL_RATE = 6

/**
 * Half the board's width is a dead zone. A board is wide and shallow, so a pointer
 * anywhere near the middle of it is not asking to go sideways.
 */
export const HORIZONTAL_DEAD_ZONE_RATIO = 0.5

/** The same reasoning down a cell, which is tall and narrow. */
export const VERTICAL_DEAD_ZONE_RATIO = 0.5

/**
 * The band at the board's own top and bottom edge inside which a drag scrolls the board
 * downwards, to reach a swimlane that is off screen.
 *
 * A fixed depth rather than a fraction of the board, and a shallow one, because this band
 * overlaps the cells it is drawn over. A cell at the edge of the board is usually clipped
 * by it, so the cell's own band is off screen and only this one is left, but where a cell
 * ends exactly at the board's edge both are in reach and both scroll. Keeping this to the
 * last stretch of the board is what makes that rare rather than the normal case.
 *
 * It does nothing on a board with no swimlanes: one row is the whole scrollport, so the
 * board does not overflow downwards and `useAutoScroll` measures no velocity for an axis
 * with no travel.
 */
export const ROOT_VERTICAL_BAND: readonly [number, number] = [64, 64]
